import React, { useState } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Button, Tabs, Table, Tag, Space, Form, Input, Select, Slider, message, Popconfirm } from 'antd';
import { 
  TeamOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined,
  CopyOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  FilterOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const AudienceTargeting = () => {
  const [activeTab, setActiveTab] = useState('saved');
  const [loading] = useState(false);

  // Saved Audiences state
  const [savedAudiences] = useState([
    { id: 1, name: 'High-Value Customers', size: '12,500', status: 'active', lastUpdated: '2024-12-01', criteria: 'Purchase > $500' },
    { id: 2, name: 'Cart Abandoners', size: '8,300', status: 'active', lastUpdated: '2024-12-02', criteria: 'Abandoned cart in last 7 days' },
    { id: 3, name: 'Newsletter Subscribers', size: '25,000', status: 'active', lastUpdated: '2024-12-03', criteria: 'Subscribed to newsletter' },
  ]);

  // Lookalike Audiences state
  const [lookalikeAudiences] = useState([
    { id: 1, name: 'Lookalike - High Value', sourceAudience: 'High-Value Customers', similarity: '1%', size: '150,000', status: 'active' },
    { id: 2, name: 'Lookalike - Converters', sourceAudience: 'Recent Purchasers', similarity: '2%', size: '300,000', status: 'active' },
  ]);

  // Custom Audience Builder state
  const [estimatedReach, setEstimatedReach] = useState(0);

  // Saved Audiences columns
  const savedColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (text) => <Text strong>{text}</Text> },
    { title: 'Size', dataIndex: 'size', key: 'size' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <Tag color={status === 'active' ? 'green' : 'default'}>{status}</Tag> },
    { title: 'Last Updated', dataIndex: 'lastUpdated', key: 'lastUpdated' },
    { title: 'Criteria', dataIndex: 'criteria', key: 'criteria', render: (text) => <Text type="secondary">{text}</Text> },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />}>Edit</Button>
          <Button size="small" icon={<CopyOutlined />}>Duplicate</Button>
          <Popconfirm title="Delete this audience?" onConfirm={() => message.success('Deleted')}>
            <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Lookalike Audiences columns
  const lookalikeColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (text) => <Text strong>{text}</Text> },
    { title: 'Source Audience', dataIndex: 'sourceAudience', key: 'sourceAudience' },
    { title: 'Similarity', dataIndex: 'similarity', key: 'similarity', render: (text) => <Tag color="blue">{text}</Tag> },
    { title: 'Size', dataIndex: 'size', key: 'size' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <Tag color={status === 'active' ? 'green' : 'default'}>{status}</Tag> },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />}>Edit</Button>
          <Popconfirm title="Delete this audience?" onConfirm={() => message.success('Deleted')}>
            <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const calculateEstimatedReach = (values) => {
    // Mock calculation based on form values
    let reach = 100000;
    if (values.ageMin || values.ageMax) reach *= 0.7;
    if (values.gender && values.gender !== 'all') reach *= 0.5;
    if (values.location) reach *= 0.6;
    if (values.interests && values.interests.length > 0) reach *= 0.4;
    setEstimatedReach(Math.floor(reach));
  };

  const tabItems = [
    {
      key: 'saved',
      label: (
        <span>
          <TeamOutlined /> Saved Audiences
        </span>
      ),
      children: (
        <div>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Title level={5} style={{ margin: 0 }}>Saved Audiences</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Manage your saved audience segments
                </Paragraph>
              </Col>
              <Col>
                <Button icon={<PlusOutlined />} type="primary" size="large">
                  Create Audience
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Audiences Table */}
          <Card>
            <Table
              columns={savedColumns}
              dataSource={savedAudiences}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'lookalike',
      label: (
        <span>
          <UsergroupAddOutlined /> Lookalike Audiences
        </span>
      ),
      children: (
        <div>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Title level={5} style={{ margin: 0 }}>Lookalike Audiences</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Find new people similar to your best customers
                </Paragraph>
              </Col>
              <Col>
                <Button icon={<PlusOutlined />} type="primary" size="large">
                  Create Lookalike
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Info Card */}
          <Card style={{ marginBottom: 24, background: '#f6f8fa' }}>
            <Space direction="vertical" size="small">
              <Text strong>What are Lookalike Audiences?</Text>
              <Paragraph type="secondary" style={{ margin: 0 }}>
                Lookalike audiences help you reach new people who are likely to be interested in your business because they're similar to your best existing customers.
              </Paragraph>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>1% similarity = Most similar, smaller audience</li>
                <li>5% similarity = Less similar, larger audience</li>
                <li>10% similarity = Least similar, largest audience</li>
              </ul>
            </Space>
          </Card>

          {/* Lookalike Table */}
          <Card>
            <Table
              columns={lookalikeColumns}
              dataSource={lookalikeAudiences}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'builder',
      label: (
        <span>
          <FilterOutlined /> Custom Audience Builder
        </span>
      ),
      children: (
        <div>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Title level={5} style={{ margin: 0 }}>Custom Audience Builder</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Build custom audiences with precise targeting criteria
                </Paragraph>
              </Col>
              <Col>
                <Button icon={<PlusOutlined />} type="primary" size="large">
                  Build New Audience
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Builder Form */}
          <Card title="Audience Criteria">
            <Form
              layout="vertical"
              onValuesChange={(_, allValues) => calculateEstimatedReach(allValues)}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Audience Name" name="name">
                    <Input placeholder="Enter audience name" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Description" name="description">
                    <Input placeholder="Describe this audience" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Title level={5} style={{ marginTop: 16 }}>Demographics</Title>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Age Range" name="ageRange">
                    <Slider range min={18} max={65} defaultValue={[25, 45]} marks={{ 18: '18', 65: '65+' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Gender" name="gender">
                    <Select size="large" defaultValue="all">
                      <Select.Option value="all">All</Select.Option>
                      <Select.Option value="male">Male</Select.Option>
                      <Select.Option value="female">Female</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Location" name="location">
                    <Select size="large" mode="multiple" placeholder="Select locations">
                      <Select.Option value="us">United States</Select.Option>
                      <Select.Option value="ca">Canada</Select.Option>
                      <Select.Option value="uk">United Kingdom</Select.Option>
                      <Select.Option value="au">Australia</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Title level={5} style={{ marginTop: 16 }}>Interests & Behaviors</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Interests" name="interests">
                    <Select size="large" mode="multiple" placeholder="Select interests">
                      <Select.Option value="tech">Technology</Select.Option>
                      <Select.Option value="fashion">Fashion</Select.Option>
                      <Select.Option value="sports">Sports</Select.Option>
                      <Select.Option value="travel">Travel</Select.Option>
                      <Select.Option value="food">Food & Dining</Select.Option>
                      <Select.Option value="fitness">Fitness</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Behaviors" name="behaviors">
                    <Select size="large" mode="multiple" placeholder="Select behaviors">
                      <Select.Option value="online-shoppers">Online Shoppers</Select.Option>
                      <Select.Option value="frequent-travelers">Frequent Travelers</Select.Option>
                      <Select.Option value="early-adopters">Early Adopters</Select.Option>
                      <Select.Option value="mobile-users">Mobile Users</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Title level={5} style={{ marginTop: 16 }}>Advanced Targeting</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Device Type" name="device">
                    <Select size="large" mode="multiple" placeholder="Select devices">
                      <Select.Option value="mobile">Mobile</Select.Option>
                      <Select.Option value="desktop">Desktop</Select.Option>
                      <Select.Option value="tablet">Tablet</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Connection Type" name="connection">
                    <Select size="large" defaultValue="all">
                      <Select.Option value="all">All Connections</Select.Option>
                      <Select.Option value="wifi">WiFi Only</Select.Option>
                      <Select.Option value="mobile">Mobile Data Only</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Estimated Reach */}
              <Card style={{ marginTop: 24, background: '#e6f7ff', borderColor: '#91d5ff' }}>
                <Row align="middle" gutter={16}>
                  <Col flex="auto">
                    <Space direction="vertical" size="small">
                      <Text strong style={{ fontSize: 16 }}>Estimated Audience Reach</Text>
                      <Text type="secondary">Based on your targeting criteria</Text>
                    </Space>
                  </Col>
                  <Col>
                    <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                      {estimatedReach.toLocaleString()}
                    </Title>
                    <Text type="secondary">people</Text>
                  </Col>
                </Row>
              </Card>

              <Form.Item style={{ marginTop: 24 }}>
                <Space>
                  <Button type="primary" size="large" icon={<UserAddOutlined />}>
                    Save Audience
                  </Button>
                  <Button size="large">
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Audience & Targeting' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Audience & Targeting</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Create and manage your audience segments for targeted campaigns
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        type="card"
      />
    </div>
  );
};

export default AudienceTargeting;
